import { Component, DestroyRef, inject, PLATFORM_ID } from '@angular/core';
import { ImagesService } from '../../../../services/general/images.service';
import {
  ProductAnalysisComponent
} from '../../../../components/card/product-management/product-analysis/product-analysis.component';
import { SearchComponent } from '../../../../shared/search/search.component';
import { FiltersComponent } from '../../../../shared/filter/filters/filters.component';
import { PaginationComponent } from '../../../../components/pagination/pagination.component';
import {
  SingleProductOverviewTableComponent
} from '../../../../components/card/product-management/single-product-overview-table/single-product-overview-table.component';
import {
  ViewNewApplicationComponent
} from '../../../../components/card/all-product/view-new-application/view-new-application.component';
import { ConfirmationDialogComponent } from '../../../../components/shared/confirmation-dialog/confirmation-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { LoanService } from '../../../../services/loan/loan.service';
import { ToastService } from '../../../../services/general/toast.service';
import { Data, PaginationInterface, } from '../../../../interfaces/responses/pagination.interface';
import { Loan, LoanAction, LoanQueryParams } from '../../../../interfaces/responses/loan.interface';
import { ProgressLoaderComponent } from '../../../../components/loaders/progress-loader/progress-loader.component';
import { ProductDetailsStore } from '../product-details.store';
import { ProductService } from '../../../../services/products/product.service';
import { Product } from '../../../../interfaces/responses/product-response.interfaces';
import { DatePipe, DecimalPipe } from '@angular/common';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { FormGroup, FormsModule } from '@angular/forms';
import { LoanStatusStylePipe, LoanStatusLabelPipe } from '../../../../pipes/loan-status.pipe';
import { PermissionService } from '../../../../services/permission/permission.service';

@Component({
  selector: 'app-overview',
  imports: [
    ProductAnalysisComponent,
    SearchComponent,
    FiltersComponent,
    PaginationComponent,
    SingleProductOverviewTableComponent,
    ProgressLoaderComponent,
    ViewNewApplicationComponent,
    ConfirmationDialogComponent,
    DatePipe,
    DecimalPipe,
    FormsModule,
    LoanStatusStylePipe,
    LoanStatusLabelPipe,
  ],
  templateUrl: './overview.component.html',
  styleUrl: './overview.component.scss',
})
export class OverviewComponent {
  _images = inject(ImagesService);
  private platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  private loanServices = inject(LoanService);
  private productService = inject(ProductService);
  permissionService = inject(PermissionService);
  private readonly route = inject(ActivatedRoute);
  private readonly _router = inject(Router);

  isLoanServiceLoading: boolean = false;
  isProductLoading: boolean = false;
  productDetailsStore = inject(ProductDetailsStore);

  productId: string = '';
  product: Product | null = null;

  loanPagination?: PaginationInterface<Loan[]>;
  loans: Loan[] = [];
  showModal = false;
  currentLoan: Loan | null = null;
  private _toast = inject(ToastService);

  // Action dialog state
  showApproveDialog = false;
  showRejectDialog = false;
  showCounterDialog = false;
  showDisburseDialog = false;
  pendingLoan: Loan | null = null;
  rejectionReason = '';
  counterForm = { comment: '', amount: null as number | null, tenure: null as number | null };

  loanQueryParams: LoanQueryParams = {
    paginate: true,
    perPage: 10,
    page: 1,
    productId: '',
    institutionId: '',
  };

  constructor() {
    // React to productId changes in the store
    // This must be called in an injection context (constructor/field initializer)
    toObservable(this.productDetailsStore.productId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((id) => {
        if (!id) return;

        console.log('OverviewComponent - productId changed:', id);
        this.productId = id;
        this.getProductDetails(id);
        this.getPaginatedProductLoans(id);
      });
  }

  ngOnInit(): void {
  }

  getProductDetails(id: string) {
    this.isProductLoading = true;
    this.product = null; // Reset product when fetching
    this.productService.getProductById(id).subscribe({
      next: (response) => {
        console.log('Product details fetched:', response);
        this.product = response;
        this.isProductLoading = false;
      },
      error: (error) => {
        console.error('Error fetching product details:', error);
        this.product = null;
        this.isProductLoading = false;
      }
    });
  }

  getPaginatedProductLoans(id: string) {
    this.isLoanServiceLoading = true;
    this.loanQueryParams.productId = id;

    this.loanServices
      .getPaginatedProductLoans(this.loanQueryParams)
      .subscribe({
        next: (resp) => {
          this.loans = resp.data;
          this.loanPagination = resp;
          this.isLoanServiceLoading = false;
        },
        error: () => {
          this.isLoanServiceLoading = false;
        },
      });
  }


  viewApplication(loan: Loan) {
    this.currentLoan = loan;
    this.showModal = true;
  }

  closeModal() {
    this.showModal = false;
    this.currentLoan = null;
  }

  handleAction(loan: Loan, action: 'APPROVE' | 'REJECT' | 'COUNTER' | 'DISBURSE') {
    this.pendingLoan = loan;
    if (action === 'APPROVE') {
      this.showApproveDialog = true;
    } else if (action === 'COUNTER') {
      this.counterForm = { comment: '', amount: null, tenure: null };
      this.showCounterDialog = true;
    } else if (action === 'DISBURSE') {
      this.showDisburseDialog = true;
    } else {
      this.rejectionReason = '';
      this.showRejectDialog = true;
    }
  }

  confirmApprove() {
    if (!this.pendingLoan) return;
    this.showApproveDialog = false;
    this.isLoanServiceLoading = true;
    this.loanServices.loanAction(this.pendingLoan.id!, { action: LoanAction.APPROVE }).subscribe({
      next: (updatedLoan) => {
        this._toast.success('Loan approved successfully');
        this.currentLoan = updatedLoan;
        this.pendingLoan = null;
        this.getPaginatedProductLoans(this.productId);
      },
      error: (err) => {
        this._toast.error(err?.message || 'Failed to approve loan');
        this.pendingLoan = null;
        this.isLoanServiceLoading = false;
      }
    });
  }

  cancelApprove() {
    this.showApproveDialog = false;
    this.pendingLoan = null;
  }

  confirmReject() {
    if (!this.pendingLoan || !this.rejectionReason.trim()) return;
    this.showRejectDialog = false;
    this.isLoanServiceLoading = true;
    this.loanServices.loanAction(this.pendingLoan.id!, { action: LoanAction.REJECT, comment: this.rejectionReason.trim() }).subscribe({
      next: (updatedLoan) => {
        this._toast.success('Loan rejected successfully');
        this.currentLoan = updatedLoan;
        this.pendingLoan = null;
        this.rejectionReason = '';
        this.getPaginatedProductLoans(this.productId);
      },
      error: (err) => {
        this._toast.error(err?.message || 'Failed to reject loan');
        this.pendingLoan = null;
        this.isLoanServiceLoading = false;
      }
    });
  }

  cancelReject() {
    this.showRejectDialog = false;
    this.pendingLoan = null;
    this.rejectionReason = '';
  }

  confirmCounter() {
    if (!this.pendingLoan || !this.counterForm.amount || !this.counterForm.tenure) return;
    this.showCounterDialog = false;
    this.isLoanServiceLoading = true;
    this.loanServices.loanAction(this.pendingLoan.id!, {
      action: LoanAction.COUNTER,
      comment: this.counterForm.comment.trim() || undefined,
      amount: this.counterForm.amount,
      tenure: this.counterForm.tenure,
    }).subscribe({
      next: (updatedLoan) => {
        this._toast.success('Counter offer submitted successfully');
        this.currentLoan = updatedLoan;
        this.pendingLoan = null;
        this.counterForm = { comment: '', amount: null, tenure: null };
        this.getPaginatedProductLoans(this.productId);
      },
      error: (err) => {
        this._toast.error(err?.message || 'Failed to submit counter offer');
        this.pendingLoan = null;
        this.isLoanServiceLoading = false;
      }
    });
  }

  cancelCounter() {
    this.showCounterDialog = false;
    this.pendingLoan = null;
    this.counterForm = { comment: '', amount: null, tenure: null };
  }

  confirmDisburse() {
    if (!this.pendingLoan) return;
    this.showDisburseDialog = false;
    this.isLoanServiceLoading = true;
    this.loanServices.loanAction(this.pendingLoan.id!, { action: LoanAction.DISBURSE }).subscribe({
      next: (updatedLoan) => {
        this._toast.success('Loan disbursed successfully');
        this.currentLoan = updatedLoan;
        this.pendingLoan = null;
        this.getPaginatedProductLoans(this.productId);
      },
      error: (err) => {
        this._toast.error(err?.message || 'Failed to disburse loan');
        this.pendingLoan = null;
        this.isLoanServiceLoading = false;
      }
    });
  }

  cancelDisburse() {
    this.showDisburseDialog = false;
    this.pendingLoan = null;
  }

  private allFetchedLoans: Loan[] = [];
  private clientFilteredLoans: Loan[] = [];
  private activeFilterValues: any = null;
  private readonly clientPerPage = 10;

  get isFilterActive(): boolean { return this.activeFilterValues !== null; }

  filterLoans(formData: FormGroup) {
    const v = formData.value;
    const hasAnyFilter = Object.values(v).some(val => val !== null && val !== undefined && val !== '');

    if (!hasAnyFilter) {
      this.resetLoanFilters();
      return;
    }

    this.activeFilterValues = v;
    this.isLoanServiceLoading = true;
    this.loanServices.getPaginatedProductLoans({ paginate: false, productId: this.productId }).subscribe({
      next: (res) => {
        this.allFetchedLoans = res.data;
        this.applyClientFilters(1);
        this.isLoanServiceLoading = false;
      },
      error: () => { this.isLoanServiceLoading = false; }
    });
  }

  private applyClientFilters(page: number) {
    const f = this.activeFilterValues;
    if (!f) return;

    this.clientFilteredLoans = this.allFetchedLoans.filter(loan => {
      if (f.status && loan.status !== f.status) return false;
      if (f.institutionId) {
        const institutionName = typeof loan.institution === 'string' ? loan.institution : loan.institution?.name;
        if ((institutionName || '').trim().toLowerCase() !== (f.institutionName || '').trim().toLowerCase()) return false;
      }
      if (f.startDate && new Date(loan.created_at || loan.createdAt!) < new Date(f.startDate)) return false;
      if (f.endDate && new Date(loan.created_at || loan.createdAt!) > new Date(f.endDate + 'T23:59:59')) return false;
      const amountValue = Number(loan.amount ?? loan.principal_amount ?? 0);
      if (f.minAmount != null && f.minAmount !== '' && amountValue < +f.minAmount) return false;
      if (f.maxAmount != null && f.maxAmount !== '' && amountValue > +f.maxAmount) return false;
      return true;
    });

    const total = this.clientFilteredLoans.length;
    const totalPages = Math.ceil(total / this.clientPerPage) || 1;
    const start = (page - 1) * this.clientPerPage;

    this.loans = this.clientFilteredLoans.slice(start, start + this.clientPerPage);
    this.loanPagination = {
      data: this.loans,
      meta: { page, perPage: this.clientPerPage, total, totalPages }
    };
  }

  resetLoanFilters() {
    this.activeFilterValues = null;
    this.allFetchedLoans = [];
    this.clientFilteredLoans = [];
    this.loanQueryParams.page = 1;
    this.getPaginatedProductLoans(this.productId);
  }

  onPageChange(page: number): void {
    if (this.isFilterActive) {
      this.applyClientFilters(page);
    } else {
      this.loanQueryParams.page = page;
      this.getPaginatedProductLoans(this.productId);
    }
  }
}
